/** 提交前业务校验（模型/应用实例共用） */
import { get } from 'lodash';

import type {
  MaasGroupSpec,
  MaasGroupedWorkloadSpec,
  MaasSharedFormValues,
  MaasSubmitError,
} from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { MaasRunMode } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { parseFormJsonField } from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import type { EmbeddedNodeSpec } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';
import { parseNodesField } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';
import {
  hasDuplicateWorkerGroupNames,
  hasWorkerGroupReservedRoleName,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeGroupValidation';

/** 校验组装后的组 resource（CRD 形态，不要求组 name） */
export function validateGroupResourceForApi(group?: MaasGroupSpec): boolean {
  if (!group?.resource) return false;
  const resource = group.resource as Record<string, unknown>;
  const cpu = resource.cpu as Record<string, unknown> | undefined;
  const memory = resource.memory as Record<string, unknown> | undefined;
  const hasCpu = !!(get(cpu, 'cores') || get(cpu, 'request') || get(cpu, 'resource.cpu.request') || get(resource, 'cpu.request'));
  const hasMem = !!(get(memory, 'request') || get(memory, 'resource.memory.request') || get(resource, 'memory.request'));
  return hasCpu && hasMem;
}

export function hasMaasInferenceSystemImage(values: Pick<MaasSharedFormValues, '_inferenceSystem'>): boolean {
  const system = parseFormJsonField<Record<string, unknown>>(values._inferenceSystem);
  if (!system) return false;
  return !!String(system.image || system.url || system.imageKey || '').trim();
}

/** 节点组 / 抽屉单节点是否已配置推理镜像 */
export function nodeHasInferenceSystemImage(node?: MaasGroupSpec): boolean {
  const system = node?.system as Record<string, unknown> | undefined;
  if (!system) return false;
  return !!String(system.image || system.url || system.imageKey || '').trim();
}

/** embedded：各节点 JSON system 须配置推理镜像 */
export function hasEmbeddedNodesInferenceImage(values: Pick<MaasSharedFormValues, 'runMode' | 'masterNodes' | 'workerNodes'>): boolean {
  const mode = values.runMode;
  const masterNodes = parseNodesField(values.masterNodes);
  const workerNodes = parseNodesField(values.workerNodes);

  if (mode === MaasRunMode.PrimarySecondary) {
    return masterNodes.some(nodeHasInferenceSystemImage) && workerNodes.length > 0 && workerNodes.every(nodeHasInferenceSystemImage);
  }
  return workerNodes.length > 0 && workerNodes.every(nodeHasInferenceSystemImage);
}

/** 按 runMode 校验 head/worker 拓扑 */
export function validateMaasRunModeTopology(
  spec: Pick<MaasGroupedWorkloadSpec, 'runMode' | 'headGroupSpec' | 'workerGroupSpecs'>,
): MaasSubmitError | undefined {
  const mode = spec.runMode;
  if (!mode) return { key: 'common:validate.required' };

  if (mode === MaasRunMode.Normal) {
    if (spec.headGroupSpec) {
      return { key: 'data-center:maas.worker.group.resource.required' };
    }
    if (!(spec.workerGroupSpecs?.length || 0)) {
      return { key: 'data-center:maas.worker.group.at.least.one' };
    }
  } else if (mode === MaasRunMode.Single) {
    if (spec.headGroupSpec) {
      return { key: 'data-center:maas.worker.group.resource.required' };
    }
    if ((spec.workerGroupSpecs?.length || 0) !== 1) {
      return { key: 'data-center:maas.worker.group.at.least.one' };
    }
    if (spec.workerGroupSpecs![0].replicas !== 1) {
      return { key: 'data-center:maas.worker.group.resource.required' };
    }
  } else if (mode === MaasRunMode.Distributed) {
    if (spec.headGroupSpec) {
      return { key: 'data-center:maas.worker.group.at.least.one' };
    }
    if (!(spec.workerGroupSpecs?.length || 0)) {
      return { key: 'data-center:maas.worker.group.at.least.one' };
    }
    if (spec.workerGroupSpecs!.length === 1 && (spec.workerGroupSpecs![0].replicas ?? 1) < 2) {
      return { key: 'data-center:maas.worker.group.distributed.topology' };
    }
  } else if (mode === MaasRunMode.PrimarySecondary) {
    if (!spec.headGroupSpec) {
      return { key: 'data-center:maas.master.group.resource.required' };
    }
    if (!(spec.workerGroupSpecs?.length || 0)) {
      return { key: 'data-center:maas.worker.group.at.least.one' };
    }
    if (hasWorkerGroupReservedRoleName(spec.workerGroupSpecs)) {
      return { key: 'data-center:maas.worker.group.name.reserved' };
    }
  }

  if (hasDuplicateWorkerGroupNames(spec.workerGroupSpecs)) {
    return { key: 'data-center:maas.worker.group.name.duplicate' };
  }

  return undefined;
}

/** 提交前将 UI 部署模式映射为后端 RunMode 枚举 */
export function resolveApiRunModeForSubmit(
  spec: Pick<MaasGroupedWorkloadSpec, 'runMode' | 'headGroupSpec' | 'workerGroupSpecs'>,
): MaasRunMode {
  const mode = spec.runMode;
  if (mode === MaasRunMode.PrimarySecondary) return MaasRunMode.PrimarySecondary;
  if (mode === MaasRunMode.Single || mode === MaasRunMode.Distributed || mode === MaasRunMode.Normal) {
    return MaasRunMode.Normal;
  }
  return MaasRunMode.Normal;
}

/** 校验各组 CPU/内存是否已配置 */
export function validateMaasGroupedResourcesForSubmit(
  spec: Pick<MaasGroupedWorkloadSpec, 'headGroupSpec' | 'workerGroupSpecs'>,
): MaasSubmitError | undefined {
  const groups = [...(spec.headGroupSpec ? [spec.headGroupSpec] : []), ...(spec.workerGroupSpecs || [])];
  for (const group of groups) {
    if (!validateGroupResourceForApi(group)) {
      if (spec.headGroupSpec && group === spec.headGroupSpec) {
        return { key: 'data-center:maas.master.group.resource.required' };
      }
      return { key: 'data-center:maas.worker.group.resource.required' };
    }
  }
  return undefined;
}

function hasConfiguredSystemDiskSize(size: unknown): boolean {
  return typeof size === 'number' && Number.isFinite(size) && size > 0;
}

/**
 * 校验节点系统盘已显式配置（与 CI「请配置系统盘」对齐；不接受静默默认 10Gi）。
 * 主从模式需 Master + Worker 均已配置；其余模式校验 Worker。
 */
/** embedded：各节点 JSON 须独立配置 _systemDiskSize */
export function validateEmbeddedNodesSystemDiskForSubmit(
  values: Pick<MaasSharedFormValues, 'runMode' | 'masterNodes' | 'workerNodes'>,
): MaasSubmitError | undefined {
  const mode = values.runMode;
  const masterNodes = parseNodesField(values.masterNodes) as EmbeddedNodeSpec[];
  const workerNodes = parseNodesField(values.workerNodes) as EmbeddedNodeSpec[];
  const nodeHasDisk = (node?: EmbeddedNodeSpec) => hasConfiguredSystemDiskSize(node?._systemDiskSize);

  if (mode === MaasRunMode.PrimarySecondary) {
    if (!masterNodes.some(nodeHasDisk) || !workerNodes.every(nodeHasDisk)) {
      return { key: 'data-center:please.add.at.least.one.system.disk', fieldKey: 'common:system.disk' };
    }
    return undefined;
  }

  if (!workerNodes.length || !workerNodes.every(nodeHasDisk)) {
    return { key: 'data-center:please.add.at.least.one.system.disk', fieldKey: 'common:system.disk' };
  }
  return undefined;
}

export function validateMaasSystemDiskForSubmit(
  values: Pick<MaasSharedFormValues, 'runMode' | 'master.systemDiskSize' | 'worker.systemDiskSize'>,
): MaasSubmitError | undefined {
  const mode = values.runMode;
  const workerConfigured = hasConfiguredSystemDiskSize(values['worker.systemDiskSize']);
  if (mode === MaasRunMode.PrimarySecondary) {
    if (!hasConfiguredSystemDiskSize(values['master.systemDiskSize']) || !workerConfigured) {
      return { key: 'data-center:please.add.at.least.one.system.disk', fieldKey: 'common:system.disk' };
    }
    return undefined;
  }
  if (!workerConfigured) {
    return { key: 'data-center:please.add.at.least.one.system.disk', fieldKey: 'common:system.disk' };
  }
  return undefined;
}
